import { Injectable } from '@angular/core';

import { Observable, of, tap, map, take, combineLatest } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { TagModel } from '../models/tag.model';
import { TagsApi } from '../../../api/tags/tags-api';
import { TagApiModel } from '../../../api/tags/tag-api.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ResourceAction } from '../../../Common/Basic/service/resource-abstract.service';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../Misc/enums/message-severity.enum';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { AllResourceAbstractService } from '../../Basic/service/all-resource-abstract.service';
import { LocalizationReplacementsModel } from '../../../Core/Localization/models/localization-replacements.model';
import { ResourceActionTypes } from '../../Basic/enums/resource-actions.enum';
import { GenericErrorCodes } from '../../Basic/service/resource-abstract.service';

@Injectable()
export class TagsService extends AllResourceAbstractService<TagModel, TagApiModel, TagModel, TagApiModel> {

    constructor(
        private tagsApi: TagsApi,
        private messageService: MessageService,
        protected localizationService: LocalizationService,
    ) {
        super();
    }

    getApiService(): MultiBaseAbstractApi<TagApiModel, TagApiModel> {
        return this.tagsApi;
    };

    constructResourceModel(apiModel: TagApiModel): TagModel {
        return new TagModel(apiModel);
    }

    constructListModel(apiModel: TagApiModel): TagModel {
        return new TagModel(apiModel);
    }

    /**
     * @param queryString the string to match against the name of the tag
     * @returns observable wrapping the TagModel[] that matches the input string or undefined if not found
     */
    quicksearchTagsByName(queryString: string): Observable<TagModel[]> {
        return this.all$.pipe(
            map((tags: TagModel[]) => {
                return tags.filter(tag => {
                    const tagName = tag.name.toLocaleLowerCase();
                    return (tagName.indexOf(queryString.toLocaleLowerCase()) !== -1);
                });
            })
        );
    }

    getTagById(id: number): Observable<TagModel> {
        return this.all$.pipe(
            map((tags: TagModel[]) => {
                return tags.find(tag => tag.id === id);
            }),
            take(1)
        );
    }

    getTagsByIds(ids: number[]): Observable<TagModel[]> {
        return this.all$.pipe(
            map((tags: TagModel[]) => {
                return tags.filter(tag => ids.includes(tag.id));
            })
        );
    }

    // Create automatically forces a refresh of the list and doesn't return that the tag has been created until the refresh is complete
    createTag(tag: TagModel): Observable<number> {
        return this.createResource(tag).pipe(
            switchMap(
                (tagNum) => {
                    return this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                        map(x => tagNum)
                    );
                }
            )
        );
    }

    /**
     * Creates a new tag without calling get all tags
     */
    createTagWithoutFetch(tag: TagModel): Observable<number> {
        return this.createResource(tag);
    }

    /**
     * Update value of an existing tag
     */
    updateTag(tag: TagModel): Observable<number> {
        return this.updateResource(tag, { actionType: ResourceActionTypes.UPDATE}).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    deleteTags(tags: TagModel[]): Observable<void | number[]> {
        const action = {
            actionType: ResourceActionTypes.DELETE,
            count: tags.length
        };
        return this.bulkDeleteResource(tags, action).pipe(
            tap(() => this.refreshAllSubject.next())
        );
    }

    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as TagModel;

        const replacement = {} as LocalizationReplacementsModel;
        replacement.name = resource?.name;
        replacement.count = actionResult?.count;

        if (error) {
            let detail = `Tags.actions.${actionType}.error.message`;

            if (error.code === GenericErrorCodes.bulkPartialFail) {
                detail = 'Tags.error.bulkPartial.message';
            }

            if (error.code === CommonApiErrorEnum.duplicateName) {
                detail = 'Tags.error.duplicate.message';
            }

            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: `Tags.actions.${actionType}.error.heading`,
                detail: this.localizationService.transform(detail, replacement)
            });

        } else {
            this.messageService.add({
                severity: messageSeverityEnum.success,
                summary: `Tags.actions.${actionType}.success.heading`,
                detail: this.localizationService.transform(`Tags.actions.${actionType}.success.message`, replacement)
            });
        }
    }

    /** @deprecated use getTagById instead, since this endpoint isn't implemented */
    override getById(id: number): Observable<TagModel> {
        return of(null);
    }

}
